import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper } from "lucide-react";
import { api, imgSrc } from "../../lib/api";
import { track } from "../../lib/analytics";

export const SectionHeading = ({ title, to, linkLabel = "Sve", id, as: H = "h2" }) => (
  <div className="sec-rule mb-5 flex items-baseline justify-between gap-4 pt-3 sm:mb-6" data-testid={id ? `section-${id}` : undefined}>
    <H className="hl text-[1.3rem] sm:text-[1.5rem] lg:text-[1.65rem]">{title}</H>
    {to && (
      <Link to={to} className="shrink-0 text-[11.5px] font-extrabold uppercase tracking-[0.12em] text-pulse hover:underline" data-testid={id ? `section-${id}-link` : undefined}>
        {linkLabel} →
      </Link>
    )}
  </div>
);

export const EmptyState = ({ title = "Još nema objavljenih tekstova", text, testId = "empty-state", compact }) => (
  <div className={`flex items-start gap-4 border border-dashed border-rule bg-surface/60 ${compact ? "p-4" : "p-6 md:p-8"}`} data-testid={testId}>
    <Newspaper className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
    <div>
      <p className="font-display text-[1.05rem] font-semibold text-ink">{title}</p>
      <p className="mt-1 max-w-prose text-sm text-muted-foreground">
        {text || "Redakcija priprema provjerene sadržaje za ovu rubriku. Novi tekstovi pojavit će se ovdje čim budu objavljeni."}
      </p>
    </div>
  </div>
);

// Ad position: renders only when an approved, active campaign is served for this slot; empty slots stay hidden.
const capKey = "bp_ad_caps";
const caps = () => { try { return JSON.parse(sessionStorage.getItem(capKey) || "{}"); } catch { return {}; } };
export const AdSlot = ({ slot, category = "", className = "", media = "" }) => {
  const [ad, setAd] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (media && !window.matchMedia(media).matches) return;
    const c = caps();
    const exclude = Object.keys(c).filter((k) => c[k].cap && c[k].n >= c[k].cap).join(",");
    api.get("/public/ads", { params: { slots: slot, category, exclude } }).then((r) => {
      const a = r.data[slot];
      if (!a) return;
      const cc = caps(); cc[a.campaign_id] = { n: (cc[a.campaign_id]?.n || 0) + 1, cap: a.frequency_cap };
      try { sessionStorage.setItem(capKey, JSON.stringify(cc)); } catch { /* ignore */ }
      setAd(a);
    }).catch(() => {});
  }, [slot, category, media]);
  useEffect(() => {
    if (!ad || !ref.current) return undefined;
    let t = null, start = 0;
    const io = new IntersectionObserver(([e]) => {
      if (e.intersectionRatio >= 0.5) { start = Date.now(); t = setTimeout(() => { track("ad_impression", { ad_id: ad.campaign_id, visible_ms: Date.now() - start }); io.disconnect(); }, 1000); }
      else clearTimeout(t);
    }, { threshold: [0, 0.5] });
    io.observe(ref.current);
    return () => { clearTimeout(t); io.disconnect(); };
  }, [ad]);
  if (!ad) return null;
  return (
    <aside ref={ref} aria-label="Oglas" className={`relative mx-auto my-6 w-fit max-w-full border border-border bg-surface p-2 pt-5 ${className}`} data-testid={`ad-slot-${slot}`}>
      <span className="absolute left-2 top-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`ad-label-${slot}`}>{ad.label} · {ad.advertiser}</span>
      <a href={`${process.env.REACT_APP_BACKEND_URL}${ad.click_url}`} target="_blank" rel="sponsored noopener noreferrer" onClick={() => track("ad_click", { ad_id: ad.campaign_id })} data-testid={`ad-link-${slot}`}>
        <img src={imgSrc(ad.image_url)} alt={ad.alt} width={ad.width} height={ad.height} loading="lazy" className="h-auto max-w-full" />
        {ad.headline && <span className="mt-1 block text-[13px] font-semibold">{ad.headline}</span>}
      </a>
    </aside>
  );
};

export const PageHeader = ({ kicker, title, intro, testId = "page-header" }) => (
  <header className="border-b border-rule pb-6 pt-8 md:pb-8 md:pt-12" data-testid={testId}>
    {kicker && <p className="kicker mb-2">{kicker}</p>}
    <h1 className="hl text-[2rem] leading-[1.1] sm:text-[2.5rem] lg:text-[2.85rem]">{title}</h1>
    {intro && <p className="dek mt-3 max-w-2xl text-[0.98rem] text-muted-foreground md:text-[1.05rem]">{intro}</p>}
  </header>
);

export const DraftNotice = ({ children }) => (
  <div role="note" className="my-6 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100" data-testid="draft-notice">
    {children}
  </div>
);
